// Pestaña "Conversación" de la pantalla del asistente: el dueño chatea con el asistente.
// Izquierda: lista de conversaciones y "Nueva conversación". Derecha: historial guardado,
// respuesta en vivo (streaming) y entrada de texto.
// Salir de la pantalla NO corta la respuesta: sigue en el servidor. Al volver, si todavía está
// respondiendo se ve en vivo; si ya terminó, aparece en el historial. "Detener" la corta.
// Acciones que el asistente propone (tramo 6): tarjeta ámbar arriba del cuadro de texto con
// "Confirmar" / "Cancelar". Escribir "confirmo" en la charla sigue funcionando igual.
// Avisos del asistente ("habla primero", 2026-10-12): tarjeta ámbar dentro del hilo con
// "Ver y seguir" / "Visto"; la lista marca "N avisos" en cada conversación con avisos nuevos.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Plus,
  Send,
  Square,
  Loader2,
  X,
  RefreshCw,
  Trash2,
  Check,
  ChevronDown,
  ChevronRight,
  Bell,
  ArrowRight,
} from 'lucide-react';
import { Can } from '../../lib/rbac';
import {
  useAssistantChat,
  type ActionOutcome,
  type ResolveClaimBody,
  type ResolveClaimResult,
} from '../../hooks/useAssistantChat';
import { useAuthStore } from '../../stores/authStore';
import {
  toolLine,
  type AlertCard,
  type ClaimOptions,
  type MessageRow,
  type PendingAlert,
  type PendingCard,
  type SessionRow,
} from './shared';
import { kindOptions, sellerOptions, sortSessionsForList } from './chatView';
import {
  CONFIRM_TEXT,
  DISPUTE_NOTE,
  NO_OPTIONS,
  OPTIONS_FAIL,
  REASON_MAX,
  RESOLUTION_LABEL,
  RESOLUTION_TONE,
  RETURN_COST_CLASS,
  isClaimCard,
  returnCostTone,
  offerLabel,
  orderedAllowed,
  reasonCounter,
} from './claimResolve';
import { confirmClickAllowed } from './decisionsView';
import { AssistantMarkdown } from '../../components/AssistantMarkdown';

const btnPrimary =
  'inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-40';
const btnGhost =
  'inline-flex items-center gap-1 px-2 py-1 text-sm rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-40';
const btnDanger =
  'inline-flex items-center gap-1 px-2 py-1 text-sm rounded bg-[var(--color-error)] text-white hover:opacity-90 disabled:opacity-40';
const btnDeleteSoft =
  'inline-flex items-center gap-1 px-2 py-1 text-sm rounded border border-red-500/30 text-red-500/80 hover:text-red-500 hover:bg-red-500/10 disabled:opacity-40';
const errorCls =
  'rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)] flex items-start justify-between gap-2';

const MIN_ROWS = 3;
const MAX_ROWS = 8;

/** Distancia al final (px) dentro de la cual el hilo sigue bajando solo al llegar texto. */
const STICK_PX = 80;

/** dd/mm hh:mm */
function fmtShort(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function ToolLine({ name }: { name: string }) {
  return <div className="text-xs text-[var(--color-text-muted)]">{toolLine(name)}</div>;
}

/** Qué pasa con el aviso del lado del asistente (línea de estado de la tarjeta). */
function autoTurnLine(card: AlertCard): { text: string; muted: boolean } | null {
  if (card.auto_turn === 'pending')
    return {
      text: 'El asistente va a revisarlo en cuanto la conversación esté libre.',
      muted: false,
    };
  if (card.auto_turn === 'started')
    return { text: 'El asistente lo está revisando…', muted: false };
  if (card.auto_turn === 'done')
    return { text: 'El asistente ya propuso algo por este aviso (ver abajo).', muted: false };
  if (card.auto_turn === 'skipped' && card.auto_turn_note)
    return { text: card.auto_turn_note, muted: true };
  return null;
}

/** Cómo quedó un aviso cerrado, según quién lo cerró. */
function closedLabel(card: AlertCard): string {
  if (card.status === 'followed') return 'Seguido';
  if (card.resolved_by === 'system') return 'Resuelto solo';
  if (card.resolved_by === 'assistant') return 'Atendido por el asistente';
  return 'Visto';
}

/** Tarjeta de un aviso que el asistente dejó por su cuenta en la conversación. */
function AlertBubble({
  card,
  onFollow,
  onDismiss,
  busy,
  anyBusy,
  streaming,
  foreign,
}: {
  card: AlertCard;
  onFollow: () => void;
  onDismiss: () => void;
  /** El botón de este aviso se está procesando. */
  busy: boolean;
  /** Algún botón de aviso se está procesando. */
  anyBusy: boolean;
  /** La conversación está respondiendo: "Ver y seguir" espera. */
  streaming: boolean;
  /** Conversación de otro usuario: sin botones. */
  foreign: boolean;
}) {
  const open = card.status === 'new' || card.status === 'seen';
  const line = autoTurnLine(card);
  const header = [card.label, card.seller_name].filter(Boolean).join(' · ');
  // Aviso sin pendientes (2026-10-14): una sola línea apagada, sin cuerpo ni botones. El dueño no
  // quiere ver avisos ya atendidos; queda la constancia de quién lo cerró y por qué.
  if (!open) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-xs text-[var(--color-text-muted)] break-words">
          <span>🔔 {header || 'Aviso'}</span>
          {card.title ? <span> · {card.title}</span> : null}
          <span className="font-medium"> · {closedLabel(card)}</span>
          {card.resolved_note ? <span>: {card.resolved_note}</span> : null}
          <span> · {fmtShort(card.created_at)}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] w-full rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 space-y-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-amber-700 dark:text-amber-400">
          <span className="font-medium">🔔 {header || 'Aviso'}</span>
          <span className="text-[var(--color-text-muted)]">{fmtShort(card.created_at)}</span>
        </div>
        {card.title && (
          <div className="text-sm font-semibold text-[var(--color-text)] break-words">
            {card.title}
          </div>
        )}
        {card.body && (
          <div className="text-sm whitespace-pre-wrap break-words text-[var(--color-text)]">
            {card.body}
          </div>
        )}
        {line && (
          <div
            className={`text-xs ${line.muted ? 'text-[var(--color-text-muted)]' : 'text-amber-700 dark:text-amber-400'}`}
          >
            {line.text}
          </div>
        )}
        {open && !foreign ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {/* Si el asistente ya propuso algo por este aviso, queda solo "Visto". */}
            {card.auto_turn !== 'done' && (
              <button className={btnPrimary} disabled={streaming || anyBusy} onClick={onFollow}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                Ver y seguir
              </button>
            )}
            <button className={btnGhost} disabled={anyBusy} onClick={onDismiss}>
              <Check size={14} />
              Visto
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Vista única de los avisos pendientes de todas las conversaciones (2026-10-21). */
function PendingAlertsPanel({
  data,
  loading,
  open,
  onToggle,
  seller,
  kind,
  onSeller,
  onKind,
  onReload,
  onFollow,
  onDismiss,
  onGo,
  busyId,
  streaming,
  currentId,
}: {
  data: { items: PendingAlert[]; total: number } | null;
  loading: boolean;
  open: boolean;
  onToggle: () => void;
  seller: string;
  kind: string;
  onSeller: (v: string) => void;
  onKind: (v: string) => void;
  onReload: () => void;
  onFollow: (sid: number, aid: number) => void;
  onDismiss: (sid: number, aid: number) => void;
  onGo: (sid: number) => void;
  busyId: number | null;
  streaming: boolean;
  currentId: number | null;
}) {
  const items = data?.items ?? [];
  const pending = items.filter((a) => a.status === 'new' || a.status === 'seen');
  const total = data?.total ?? 0;
  const sellers = sellerOptions(items);
  const kinds = kindOptions(items);
  const anyBusy = busyId !== null;
  const sel =
    'text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)] px-1.5 py-1';
  return (
    <div className="rounded-lg border border-amber-500/40 bg-[var(--color-bg-card)]">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <button
          className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]"
          onClick={onToggle}
          aria-expanded={open}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Bell size={16} className="text-amber-600 dark:text-amber-400" />
          Avisos pendientes {data ? `(${total})` : ''}
          {loading && <Loader2 size={14} className="animate-spin" />}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={sel}
            value={seller}
            onChange={(e) => onSeller(e.target.value)}
            aria-label="Empresa"
          >
            <option value="">Todas las empresas</option>
            {sellers.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} ({o.count})
              </option>
            ))}
            {seller && !sellers.some((o) => o.value === seller) && (
              <option value={seller}>Empresa {seller}</option>
            )}
          </select>
          <select
            className={sel}
            value={kind}
            onChange={(e) => onKind(e.target.value)}
            aria-label="Tipo de aviso"
          >
            <option value="">Todos los tipos</option>
            {kinds.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} ({o.count})
              </option>
            ))}
            {kind && !kinds.some((o) => o.value === kind) && <option value={kind}>{kind}</option>}
          </select>
          <button
            className={btnGhost}
            disabled={loading}
            onClick={onReload}
            title="Actualizar los avisos"
            aria-label="Actualizar los avisos"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[var(--color-border)] px-3 py-2 space-y-2 max-h-[45vh] overflow-y-auto">
          {!data && loading ? (
            <div className="text-sm text-[var(--color-text-muted)]">Cargando avisos…</div>
          ) : pending.length === 0 ? (
            <div className="text-sm text-[var(--color-text-muted)]">
              No hay avisos pendientes{seller || kind ? ' con estos filtros' : ''}.
            </div>
          ) : (
            pending.map((a) => {
              const header = [a.label, a.seller_name].filter(Boolean).join(' · ');
              const line = autoTurnLine(a);
              const busy = busyId === a.id;
              const sid = a.session_id;
              return (
                <div
                  key={a.id}
                  className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 space-y-1.5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-amber-700 dark:text-amber-400">
                    <span className="font-medium">🔔 {header || 'Aviso'}</span>
                    <span className="text-[var(--color-text-muted)]">{fmtShort(a.created_at)}</span>
                  </div>
                  {a.title && (
                    <div className="text-sm font-semibold text-[var(--color-text)] break-words">
                      {a.title}
                    </div>
                  )}
                  {a.body && (
                    <div className="text-sm whitespace-pre-wrap break-words text-[var(--color-text)]">
                      {a.body}
                    </div>
                  )}
                  {line && (
                    <div
                      className={`text-xs ${line.muted ? 'text-[var(--color-text-muted)]' : 'text-amber-700 dark:text-amber-400'}`}
                    >
                      {line.text}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {sid != null && a.auto_turn !== 'done' && (
                      <button
                        className={btnPrimary}
                        disabled={anyBusy || (currentId === sid && streaming)}
                        onClick={() => onFollow(sid, a.id)}
                      >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                        Ver y seguir
                      </button>
                    )}
                    {sid != null && (
                      <button
                        className={btnGhost}
                        disabled={anyBusy}
                        onClick={() => onDismiss(sid, a.id)}
                      >
                        <Check size={14} />
                        Visto
                      </button>
                    )}
                    {sid != null && (
                      <button
                        className={btnGhost}
                        disabled={currentId === sid}
                        onClick={() => onGo(sid)}
                        title={a.session_title ?? undefined}
                      >
                        <ArrowRight size={14} />
                        {currentId === sid
                          ? 'Es esta conversación'
                          : `Ir a la conversación${a.session_title ? `: ${a.session_title}` : ''}`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function Bubble({
  m,
  alerts,
  onFollow,
  onDismiss,
  busy,
  streaming,
  foreign,
}: {
  m: MessageRow;
  /** Estado vivo de los avisos (pisa al guardado en el mensaje). */
  alerts: Record<number, AlertCard>;
  onFollow: (id: number) => void;
  onDismiss: (id: number) => void;
  /** Aviso cuyo botón se está procesando. */
  busy: number | null;
  streaming: boolean;
  foreign: boolean;
}) {
  const c = m.content as Record<string, unknown>;
  if (m.role === 'system') {
    // Aviso del asistente: tarjeta con botones.
    if (c.alert && typeof c.alert === 'object') {
      const saved = c.alert as AlertCard;
      const card = alerts[saved.id] ?? saved;
      return (
        <AlertBubble
          card={card}
          onFollow={() => onFollow(card.id)}
          onDismiss={() => onDismiss(card.id)}
          busy={busy === card.id}
          anyBusy={busy !== null}
          streaming={streaming}
          foreign={foreign}
        />
      );
    }
    // Aviso de una acción confirmada/cancelada desde la tarjeta o vencida: línea discreta.
    if (typeof c.notice === 'string' && c.notice) {
      return (
        <div className="flex justify-center">
          <div className="max-w-[85%] rounded-full px-3 py-1 text-xs text-center break-words border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]">
            {c.notice}
          </div>
        </div>
      );
    }
    const err = typeof c.error === 'string' ? c.error : typeof c.text === 'string' ? c.text : '';
    return <div className="text-center text-xs text-[var(--color-text-muted)]">Error: {err}</div>;
  }
  if (m.role === 'user') {
    const text = typeof c.text === 'string' ? c.text : '';
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words bg-blue-500/10 border border-blue-500/20 text-[var(--color-text)]">
          {text}
        </div>
      </div>
    );
  }
  // assistant: bloques de texto y líneas de herramientas, en orden.
  const blocks = Array.isArray(c.blocks)
    ? (c.blocks as Array<{ type: string; text?: string; name?: string }>)
    : typeof c.text === 'string'
      ? [{ type: 'text', text: c.text }]
      : [];
  if (blocks.length === 0) return null;
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] space-y-1">
        {blocks.map((b, i) =>
          b.type === 'tool_use' ? (
            <ToolLine key={i} name={b.name ?? ''} />
          ) : b.type === 'text' && b.text ? (
            <div
              key={i}
              className="rounded-lg px-3 py-2 text-sm break-words bg-[var(--color-bg-secondary)] text-[var(--color-text)]"
            >
              <AssistantMarkdown text={b.text} />
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}

const btnConfirm =
  'inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-40';

/** Minutos que faltan hasta `iso` (redondeado hacia arriba; 0 si ya pasó). */
function minutesLeft(iso: string, now: number): number {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.ceil((t - now) / 60_000));
}

const ACTION_STATUS: Record<string, string> = {
  confirmed: 'En curso',
  executed: 'Hecha',
  failed: 'Falló',
  cancelled: 'Cancelada',
  expired: 'Vencida',
  superseded: 'Ya resuelta por otra vía',
  pending: 'Pendiente',
};

/** Color de fondo de cada botón de resolución (mismos que en Órdenes). */
const TONE_BG: Record<string, string> = {
  warning: 'var(--color-warning, #f59e0b)',
  error: 'var(--color-error, #ef4444)',
  violet: '#8b5cf6',
  info: 'var(--color-info, #3b82f6)',
};
const btnTone =
  'inline-flex items-center gap-1 px-2 py-1 text-sm rounded text-white hover:opacity-90 disabled:opacity-40';

type ResolveStep =
  | null
  | { kind: string; step: 'panel' }
  | { kind: string; step: 'confirm'; armedAt: number };

/**
 * Fila "Resolver el reclamo" de una tarjeta de reclamo: consulta una vez qué permite Mercado
 * Libre ahora y muestra un botón por cada opción. Todas piden confirmación; el parcial elige
 * porcentaje y escalar admite un motivo. El servidor ejecuta tras el sí.
 */
function ClaimResolveRow({
  pid,
  anyBusy,
  onLoad,
  onResolve,
}: {
  pid: number;
  anyBusy: boolean;
  onLoad: (pid: number) => Promise<ClaimOptions | null>;
  onResolve: (pid: number, body: ResolveClaimBody) => Promise<ResolveClaimResult | null>;
}) {
  const [opts, setOpts] = useState<ClaimOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [step, setStep] = useState<ResolveStep>(null);
  const [percentage, setPercentage] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<ResolveClaimResult | null>(null);
  /** Tras una resolución exitosa la fila se oculta (queda solo la línea del resultado). */
  const [done, setDone] = useState(false);
  const alive = useRef(true);
  const seq = useRef(0);

  const refresh = async () => {
    const n = ++seq.current;
    setLoading(true);
    const r = await onLoad(pid);
    if (!alive.current || n !== seq.current) return;
    setOpts(r);
    setFailed(r === null);
    setLoading(false);
  };

  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => {
      alive.current = false;
    };
    // Una sola consulta al montar; después, solo con "Actualizar".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  const run = async (kind: string) => {
    if (running) return;
    const body: ResolveClaimBody = { kind };
    if (kind === 'claim_partial_refund') {
      if (percentage == null) return;
      body.percentage = percentage;
    }
    if (kind === 'open_dispute' && reason.trim()) body.reason = reason.trim().slice(0, REASON_MAX);
    setRunning(true);
    const r = await onResolve(pid, body);
    if (!alive.current) return;
    setRunning(false);
    if (!r) return;
    setOutcome(r);
    setStep(null);
    if (r.executed) setDone(true);
    else void refresh();
  };

  const outcomeLine = outcome && (
    <div className={`text-xs ${outcome.executed ? 'text-green-600' : 'text-[var(--color-error)]'}`}>
      {outcome.text}
    </div>
  );

  if (done) return outcomeLine;
  if (opts?.no_permission) return outcomeLine;

  const locked = anyBusy || running;
  const allowed = opts ? orderedAllowed(opts.allowed ?? []) : [];
  const confirmBack = (kind: string) =>
    setStep(
      kind === 'claim_partial_refund' || kind === 'open_dispute' ? { kind, step: 'panel' } : null
    );

  let body: React.ReactNode;
  if (loading && !opts) {
    body = (
      <span className="text-xs text-[var(--color-text-muted)] inline-flex items-center gap-1">
        <Loader2 size={12} className="animate-spin" /> Consultando a Mercado Libre…
      </span>
    );
  } else if (failed || !opts) {
    body = <span className="text-xs text-[var(--color-text-muted)]">{OPTIONS_FAIL}</span>;
  } else if (allowed.length === 0) {
    body = (
      <span className="text-xs text-[var(--color-text-muted)]">{opts.warning || NO_OPTIONS}</span>
    );
  } else if (step?.step === 'confirm') {
    const armedAt = step.armedAt;
    const kind = step.kind;
    body = (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[var(--color-text)]">{CONFIRM_TEXT[kind]}</span>
        {kind === 'allow_return' && opts.return_cost_text && (
          <span
            className={`basis-full text-xs ${RETURN_COST_CLASS[returnCostTone(opts.return_cost)]}`}
          >
            {opts.return_cost_text}
          </span>
        )}
        <button
          className={btnConfirm}
          disabled={locked}
          onClick={() => {
            if (confirmClickAllowed(armedAt, Date.now())) void run(kind);
          }}
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Sí, hacerlo
        </button>
        <button className={btnGhost} disabled={locked} onClick={() => confirmBack(kind)}>
          No, volver
        </button>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-wrap items-center gap-2">
        {allowed.map((k) => (
          <button
            key={k}
            className={btnTone}
            style={{ background: TONE_BG[RESOLUTION_TONE[k]] }}
            disabled={locked}
            onClick={() =>
              setStep(
                k === 'claim_partial_refund' || k === 'open_dispute'
                  ? step?.kind === k
                    ? null
                    : { kind: k, step: 'panel' }
                  : { kind: k, step: 'confirm', armedAt: Date.now() }
              )
            }
          >
            {RESOLUTION_LABEL[k]}
          </button>
        ))}
      </div>
    );
  }

  const panel =
    opts && step?.step === 'panel' ? (
      step.kind === 'claim_partial_refund' ? (
        <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2 space-y-2">
          {opts.offers.length === 0 ? (
            <div className="text-xs text-[var(--color-text-muted)]">
              Mercado Libre no ofrece porcentajes de reembolso para este reclamo ahora.
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {opts.offers.map((o) => (
                <label key={o.percentage} className="inline-flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    name={`partial-${pid}`}
                    checked={percentage === o.percentage}
                    disabled={locked}
                    onChange={() => setPercentage(o.percentage)}
                  />
                  {offerLabel(o)}
                </label>
              ))}
            </div>
          )}
          <button
            className={btnTone}
            style={{ background: TONE_BG.violet }}
            disabled={locked || percentage == null}
            onClick={() =>
              setStep({ kind: 'claim_partial_refund', step: 'confirm', armedAt: Date.now() })
            }
          >
            {percentage == null ? 'Enviar oferta' : `Enviar oferta del ${percentage} %`}
          </button>
        </div>
      ) : (
        <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2 space-y-2">
          <div className="text-sm text-[var(--color-text)]">{DISPUTE_NOTE}</div>
          <label className="block text-xs text-[var(--color-text-secondary)]">
            ¿Por qué lo escalás? (opcional; ayuda al asistente)
            <textarea
              className="mt-1 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-sm text-[var(--color-text)]"
              rows={3}
              maxLength={REASON_MAX}
              value={reason}
              disabled={locked}
              onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
            />
          </label>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-[var(--color-text-muted)]">{reasonCounter(reason)}</span>
            <button
              className={btnTone}
              style={{ background: TONE_BG.info }}
              disabled={locked}
              onClick={() =>
                setStep({ kind: 'open_dispute', step: 'confirm', armedAt: Date.now() })
              }
            >
              Escalar a Mercado Libre
            </button>
          </div>
        </div>
      )
    ) : null;

  return (
    <div className="border-t border-amber-500/30 pt-2 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-[var(--color-text)]">Resolver el reclamo:</span>
        <button
          className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
          disabled={locked || loading}
          onClick={() => {
            setStep(null);
            void refresh();
          }}
          title="Volver a consultar a Mercado Libre"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Actualizar opciones
        </button>
      </div>
      {opts?.cases_warning && (
        <div className="text-xs text-amber-700 dark:text-amber-400">{opts.cases_warning}</div>
      )}
      {body}
      {panel}
      {opts?.warning && allowed.length > 0 && (
        <div className="text-xs text-amber-700 dark:text-amber-400">{opts.warning}</div>
      )}
      {outcomeLine}
    </div>
  );
}

/** Tarjeta ámbar de una acción que espera confirmación del dueño. */
function PendingActionCard({
  a,
  now,
  readOnly,
  busy,
  anyBusy,
  onConfirm,
  onCancel,
  onLoadClaimOptions,
  onResolveClaim,
}: {
  a: PendingCard;
  now: number;
  /** Conversación de otro usuario: solo se muestra, sin botones. */
  readOnly: boolean;
  busy: boolean;
  anyBusy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onLoadClaimOptions: (pid: number) => Promise<ClaimOptions | null>;
  onResolveClaim: (pid: number, body: ResolveClaimBody) => Promise<ResolveClaimResult | null>;
}) {
  const [armed, setArmed] = useState(false);
  const mins = minutesLeft(a.expires_at, now);
  const expired = mins <= 0;
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-[var(--color-text)]">
          {a.label} <span className="font-normal text-[var(--color-text-muted)]">#{a.id}</span>
        </div>
        <div className="text-xs text-amber-700 dark:text-amber-400">
          {expired ? 'Venció' : `Vence en ${mins} min`}
          {a.irreversible && ' · no se puede deshacer'}
        </div>
      </div>
      <div className="rounded bg-[var(--color-bg-card)] border border-[var(--color-border)] px-3 py-2 text-sm break-words text-[var(--color-text)] max-h-[40vh] overflow-y-auto">
        <AssistantMarkdown text={a.preview} />
      </div>
      {a.verificado && !a.preview.includes(a.verificado) && (
        <div className="text-xs text-[var(--color-text-muted)]">{a.verificado}</div>
      )}
      {readOnly ? (
        <div className="text-xs text-[var(--color-text-muted)]">
          Solo quien abrió la conversación puede confirmar esta acción.
        </div>
      ) : !a.can_confirm ? (
        <div className="text-xs text-[var(--color-text-muted)]">
          No tenés permiso para confirmar esta acción.
        </div>
      ) : expired ? (
        <div className="text-xs text-[var(--color-text-muted)]">
          Venció: pedile al asistente que la proponga de nuevo.
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {a.irreversible && armed ? (
            <>
              <button className={btnConfirm} disabled={anyBusy} onClick={onConfirm}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                ¿Seguro? Confirmar
              </button>
              <button className={btnGhost} disabled={anyBusy} onClick={() => setArmed(false)}>
                No, volver
              </button>
            </>
          ) : (
            <>
              <button
                className={btnConfirm}
                disabled={anyBusy}
                onClick={() => (a.irreversible ? setArmed(true) : onConfirm())}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Confirmar
              </button>
              <button className={btnGhost} disabled={anyBusy} onClick={onCancel}>
                <X size={14} />
                Cancelar
              </button>
            </>
          )}
        </div>
      )}
      {!readOnly && a.can_confirm && !expired && isClaimCard(a) && (
        <ClaimResolveRow
          pid={a.id}
          anyBusy={anyBusy}
          onLoad={onLoadClaimOptions}
          onResolve={onResolveClaim}
        />
      )}
    </div>
  );
}

/** Acción ya cerrada (hecha, fallida, cancelada o vencida), en gris. */
function RecentActionCard({ a }: { a: PendingCard }) {
  const ok = a.status === 'executed';
  const bad = a.status === 'failed';
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-2 space-y-1 text-[var(--color-text-muted)]">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="font-medium text-[var(--color-text-secondary)]">
          {a.label} #{a.id}
        </span>
        <span>
          {ACTION_STATUS[a.status] ?? a.status} · {fmtShort(a.executed_at ?? a.created_at)}
        </span>
      </div>
      {(ok || bad) && a.detail && (
        <div className={`text-xs ${ok ? 'text-green-600' : 'text-[var(--color-error)]'}`}>
          {ok ? `✓ Hecho: ${a.detail}` : `✗ ${a.detail}`}
        </div>
      )}
      <details className="text-xs">
        <summary className="cursor-pointer select-none">Ver lo que se propuso</summary>
        <div className="mt-1 text-sm break-words">
          <AssistantMarkdown text={a.preview} />
        </div>
      </details>
      {a.verificado && !a.preview.includes(a.verificado) && (
        <div className="text-xs text-[var(--color-text-muted)]">{a.verificado}</div>
      )}
    </div>
  );
}

function ActionsPanel({
  pending,
  recent,
  outcome,
  onDismissOutcome,
  readOnly,
  busyId,
  resolvingId,
  onConfirm,
  onCancel,
  onLoadClaimOptions,
  onResolveClaim,
}: {
  pending: PendingCard[];
  recent: PendingCard[];
  outcome: ActionOutcome | null;
  onDismissOutcome: () => void;
  readOnly: boolean;
  busyId: number | null;
  /** Tarjeta con una resolución de reclamo en curso (bloquea todo, sin spinner en Confirmar). */
  resolvingId: number | null;
  onConfirm: (id: number) => void;
  onCancel: (id: number) => void;
  onLoadClaimOptions: (pid: number) => Promise<ClaimOptions | null>;
  onResolveClaim: (pid: number, body: ResolveClaimBody) => Promise<ResolveClaimResult | null>;
}) {
  const [now, setNow] = useState(() => Date.now());
  const [showRecent, setShowRecent] = useState(false);
  // Reloj para "vence en N min" mientras haya tarjetas pendientes.
  useEffect(() => {
    if (pending.length === 0) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [pending.length]);

  if (pending.length === 0 && recent.length === 0 && !outcome) return null;
  return (
    <div className="border-t border-[var(--color-border)] px-3 pt-3 space-y-2">
      {outcome && (
        <div
          className={`rounded-lg p-2 text-sm flex items-start justify-between gap-2 ${
            outcome.ok
              ? 'bg-green-500/10 text-green-700 dark:text-green-400'
              : 'bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]'
          }`}
        >
          <span>
            {outcome.label} #{outcome.id}:{' '}
            {outcome.ok ? `✓ Hecho: ${outcome.text}` : `✗ ${outcome.text}`}
          </span>
          <button
            onClick={onDismissOutcome}
            title="Ocultar aviso"
            aria-label="Ocultar aviso"
            className="shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {pending.map((a) => (
        <PendingActionCard
          key={a.id}
          a={a}
          now={now}
          readOnly={readOnly}
          busy={busyId === a.id}
          anyBusy={busyId !== null || resolvingId !== null}
          onConfirm={() => onConfirm(a.id)}
          onCancel={() => onCancel(a.id)}
          onLoadClaimOptions={onLoadClaimOptions}
          onResolveClaim={onResolveClaim}
        />
      ))}
      {recent.length > 0 && (
        <div>
          <button
            className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            onClick={() => setShowRecent((v) => !v)}
          >
            {showRecent ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Acciones recientes ({recent.length})
          </button>
          {showRecent && (
            <div className="mt-2 space-y-2 max-h-[30vh] overflow-y-auto">
              {recent.map((a) => (
                <RecentActionCard key={a.id} a={a} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SessionItem({
  s,
  active,
  live,
  me,
  onClick,
  canDelete,
  confirming,
  deleteDisabled,
  onAskDelete,
  onCancelDelete,
  onDelete,
}: {
  s: SessionRow;
  active: boolean;
  /** Se está respondiendo en esta conversación. */
  live: boolean;
  me: string | null;
  onClick: () => void;
  /** Conversación propia: se puede eliminar. */
  canDelete: boolean;
  /** Se está pidiendo confirmar la eliminación de esta fila. */
  confirming: boolean;
  deleteDisabled: boolean;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
}) {
  const closed = s.status === 'closed';
  if (confirming) {
    return (
      <div className="px-3 py-2 rounded text-sm border border-red-500/30 bg-red-500/5 space-y-1">
        <div className="truncate font-medium text-[var(--color-text)]">
          {s.title || 'Sin título'}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <button className={btnDanger} disabled={deleteDisabled} onClick={onDelete}>
            ¿Seguro? Se borra para siempre · Eliminar
          </button>
          <button className={btnGhost} onClick={onCancelDelete}>
            Cancelar
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="group relative">
      <button
        onClick={onClick}
        className={`w-full text-left px-3 py-2 ${canDelete ? 'pr-9' : ''} rounded text-sm transition-colors ${
          active
            ? 'bg-[var(--color-primary)] text-white'
            : closed
              ? 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]'
              : 'text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]'
        }`}
      >
        <div className="truncate font-medium">{s.title || 'Sin título'}</div>
        <div
          className={`text-xs flex items-center gap-2 ${active ? 'text-white/80' : 'text-[var(--color-text-muted)]'}`}
        >
          <span>{fmtShort(s.last_at)}</span>
          {me && s.username !== me && <span className="truncate">de {s.username}</span>}
          {closed && <span className="px-1 rounded border border-current">cerrada</span>}
          {(s.alerts_new ?? 0) > 0 && (
            <span
              className={`px-1 rounded ${active ? 'bg-white/20 text-white' : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'}`}
            >
              {s.alerts_new === 1 ? '1 aviso' : `${s.alerts_new} avisos`}
            </span>
          )}
          {live && !closed && (
            <span className="inline-flex items-center gap-1">
              <Loader2 size={10} className="animate-spin" />
              respondiendo…
            </span>
          )}
        </div>
      </button>
      {canDelete && (
        <Can cap="assistant.chat">
          <button
            className={`absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded disabled:opacity-40 ${
              active
                ? 'text-white/80 hover:text-white hover:bg-white/10'
                : 'text-red-500/70 hover:text-red-500 hover:bg-red-500/10'
            }`}
            disabled={deleteDisabled}
            onClick={onAskDelete}
            title="Eliminar conversación"
            aria-label="Eliminar conversación"
          >
            <Trash2 size={14} />
          </button>
        </Can>
      )}
    </div>
  );
}

export default function ChatTab() {
  const chat = useAssistantChat();
  const {
    sessions,
    sessionsLoading,
    current,
    messages,
    historyLoading,
    streaming,
    sending,
    liveText,
    liveTools,
    lastUsage,
    error,
    creating,
    setError,
    loadSessions,
    createSession,
    openSession,
    closeSession,
    deleteSession,
    send,
    stop,
    pendingActions,
    recentActions,
    actionBusy,
    resolvingBusy,
    actionOutcome,
    setActionOutcome,
    loadActions,
    confirmAction,
    cancelAction,
    loadClaimOptions,
    resolveClaim,
    alerts,
    alertBusy,
    followAlert,
    dismissAlert,
    sessionsLimit,
    loadMoreSessions,
    pendingAlerts,
    pendingAlertsLoading,
    loadPendingAlerts,
    followAlertIn,
    dismissAlertIn,
  } = chat;

  const [draft, setDraft] = useState('');
  /** Confirmación pendiente en la cabecera de la conversación abierta. */
  const [confirmHead, setConfirmHead] = useState<'close' | 'delete' | null>(null);
  /** Fila de la lista con la confirmación de eliminar abierta. */
  const [confirmRowId, setConfirmRowId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const threadRef = useRef<HTMLDivElement | null>(null);
  /** true si el usuario está al final del hilo (o cerca): solo entonces se baja solo. */
  const stickRef = useRef(true);
  const me = useAuthStore((st) => st.user?.username ?? null);
  /** Lista de conversaciones: solo las que tienen avisos pendientes (2026-10-21). */
  const [onlyWithAlerts, setOnlyWithAlerts] = useState(false);
  /** Vista única de avisos: desplegada, y filtros por empresa y tipo (2026-10-21). */
  const [alertsOpen, setAlertsOpen] = useState(true);
  const [alertSeller, setAlertSeller] = useState<string>('');
  const [alertKind, setAlertKind] = useState<string>('');

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  // Vista única de avisos: se carga al entrar, al cambiar un filtro y cada vez que cambia la
  // cantidad de avisos pendientes de la lista (llegó uno nuevo o se atendió uno).
  const pendingCount = sessions.reduce((n, x) => n + (x.alerts_new ?? 0), 0);
  useEffect(() => {
    void loadPendingAlerts({
      seller_id: alertSeller ? Number(alertSeller) : null,
      kind: alertKind || null,
    });
  }, [loadPendingAlerts, alertSeller, alertKind, pendingCount]);

  const listed = useMemo(
    () => sortSessionsForList(sessions, onlyWithAlerts),
    [sessions, onlyWithAlerts]
  );

  // Enlace directo `/assistant?session=<id>` (p. ej. "Preguntar al asistente" desde el panel del
  // reclamo): se abre esa conversación UNA vez al montar (gana sobre la última recordada, que se
  // abrió antes) y se quita el parámetro de la dirección para que recargar no la reabra.
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current) return;
    deepLinkDone.current = true;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('session');
    if (raw === null) return;
    const id = Number(raw);
    if (Number.isInteger(id) && id > 0) void openSession(id);
    params.delete('session');
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
    );
  }, [openSession]);

  // Mientras alguna conversación está respondiendo, refrescar la lista para que la marca
  // "respondiendo…" se apague sola al terminar.
  const anyLive = sessions.some((s) => s.turn_active);
  useEffect(() => {
    if (!anyLive) return;
    const t = setInterval(() => void loadSessions(), 20_000);
    return () => clearInterval(t);
  }, [anyLive, loadSessions]);

  // Al cambiar de conversación, se descarta la confirmación pendiente de la cabecera.
  useEffect(() => {
    setConfirmHead(null);
  }, [current?.id]);

  const doDelete = async (id: number) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteSession(id);
    } finally {
      setDeleting(false);
      setConfirmRowId(null);
      setConfirmHead(null);
    }
  };

  // Auto-scroll SOLO del hilo (nunca de la página) y solo si el usuario estaba cerca del final.
  // Al abrir otra conversación se fuerza el enganche en el MISMO paso de dibujo (un efecto
  // aparte corría después de este y la primera bajada se perdía si el usuario había subido).
  const seenIdRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const id = current?.id ?? null;
    if (id !== seenIdRef.current) {
      seenIdRef.current = id;
      stickRef.current = true;
    }
    const el = threadRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [current?.id, messages, liveText, liveTools, streaming, historyLoading]);

  const onThreadScroll = () => {
    const el = threadRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_PX;
  };

  const closed = current?.status === 'closed';
  // Conversación de otro usuario (el administrador ve todas): solo lectura.
  const foreign = !!current && !!me && current.username !== me;
  const canSend = draft.trim().length > 0 && !streaming && !creating && !closed && !foreign;

  const doSend = async () => {
    if (!canSend) return;
    const text = draft;
    setDraft('');
    stickRef.current = true;
    const back = await send(text);
    // Rechazado antes de responder: el texto vuelve al cuadro para reintentar.
    if (back) setDraft((d) => (d.trim() ? d : back));
  };

  const rows = Math.min(MAX_ROWS, Math.max(MIN_ROWS, draft.split('\n').length));

  // Una tarjeta que vence mientras se mira: se recarga para que pase a "recientes".
  const nextExpiry = pendingActions.reduce<number | null>((min, a) => {
    const t = new Date(a.expires_at).getTime();
    return Number.isNaN(t) ? min : min === null ? t : Math.min(min, t);
  }, null);
  useEffect(() => {
    if (nextExpiry === null) return;
    const wait = Math.max(1_000, nextExpiry - Date.now() + 2_000);
    if (wait > 2 ** 31 - 1) return;
    const t = setTimeout(() => void loadActions(), wait);
    return () => clearTimeout(t);
  }, [nextExpiry, loadActions]);

  return (
    <div className="space-y-4">
      <PendingAlertsPanel
        data={pendingAlerts}
        loading={pendingAlertsLoading}
        open={alertsOpen}
        onToggle={() => setAlertsOpen((v) => !v)}
        seller={alertSeller}
        kind={alertKind}
        onSeller={setAlertSeller}
        onKind={setAlertKind}
        onReload={() =>
          void loadPendingAlerts({
            seller_id: alertSeller ? Number(alertSeller) : null,
            kind: alertKind || null,
          })
        }
        onFollow={(sid, aid) => void followAlertIn(sid, aid)}
        onDismiss={(sid, aid) => void dismissAlertIn(sid, aid)}
        onGo={(sid) => {
          if (!sending && current?.id !== sid) void openSession(sid);
        }}
        busyId={alertBusy}
        streaming={streaming}
        currentId={current?.id ?? null}
      />
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Lista de conversaciones */}
        <div className="lg:w-[280px] lg:shrink-0 space-y-2">
          <div className="flex items-center gap-2">
            <button
              className={btnPrimary}
              disabled={sending || creating}
              onClick={() => void createSession()}
            >
              <Plus size={14} />
              Nueva conversación
            </button>
            <button
              className={btnGhost}
              disabled={sessionsLoading}
              onClick={() => void loadSessions()}
              title="Actualizar la lista"
              aria-label="Actualizar la lista"
            >
              <RefreshCw size={14} className={sessionsLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-1 space-y-1 max-h-[30vh] lg:max-h-[70vh] overflow-y-auto">
            {sessions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-[var(--color-text-muted)]">
                {sessionsLoading ? 'Cargando…' : 'Todavía no hay conversaciones.'}
              </div>
            ) : (
              listed.map((s) => (
                <SessionItem
                  key={s.id}
                  s={s}
                  active={current?.id === s.id}
                  live={!!s.turn_active || (current?.id === s.id && streaming)}
                  me={me}
                  onClick={() => {
                    if (!sending && current?.id !== s.id) void openSession(s.id);
                  }}
                  canDelete={!!me && s.username === me}
                  confirming={confirmRowId === s.id}
                  deleteDisabled={deleting || (sending && current?.id === s.id)}
                  onAskDelete={() => {
                    setConfirmHead(null);
                    setConfirmRowId(s.id);
                  }}
                  onCancelDelete={() => setConfirmRowId(null)}
                  onDelete={() => void doDelete(s.id)}
                />
              ))
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <label className="inline-flex items-center gap-1 text-[var(--color-text-muted)] cursor-pointer">
              <input
                type="checkbox"
                checked={onlyWithAlerts}
                onChange={(e) => setOnlyWithAlerts(e.target.checked)}
              />
              Solo con avisos
            </label>
            {sessions.length >= sessionsLimit && sessionsLimit < 500 && (
              <button
                className={btnGhost}
                disabled={sessionsLoading}
                onClick={() => void loadMoreSessions()}
              >
                Mostrar más
              </button>
            )}
            <span className="text-[var(--color-text-muted)]">
              {listed.length} de {sessions.length} cargadas
            </span>
          </div>
        </div>

        {/* Hilo */}
        <div className="flex-1 min-w-0 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] flex flex-col">
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-[var(--color-border)]">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[var(--color-text)] truncate">
                {current ? current.title || 'Sin título' : 'Conversación nueva'}
              </div>
              {current && (
                <div className="text-xs text-[var(--color-text-muted)]">
                  {closed
                    ? 'Conversación cerrada'
                    : `Última actividad: ${fmtShort(current.last_at)}`}
                </div>
              )}
            </div>
            {current && !foreign && (
              <Can cap="assistant.chat">
                {confirmHead === 'close' ? (
                  <div className="flex items-center gap-1">
                    <button
                      className={btnDanger}
                      disabled={streaming}
                      onClick={() => {
                        setConfirmHead(null);
                        void closeSession(current.id);
                      }}
                    >
                      ¿Seguro? Cerrar
                    </button>
                    <button className={btnGhost} onClick={() => setConfirmHead(null)}>
                      Cancelar
                    </button>
                  </div>
                ) : confirmHead === 'delete' ? (
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    <button
                      className={btnDanger}
                      disabled={deleting || sending}
                      onClick={() => void doDelete(current.id)}
                    >
                      <Trash2 size={14} />
                      ¿Seguro? Se borra para siempre · Eliminar
                    </button>
                    <button className={btnGhost} onClick={() => setConfirmHead(null)}>
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {!closed && (
                      <button
                        className={btnGhost}
                        disabled={streaming}
                        onClick={() => setConfirmHead('close')}
                      >
                        <X size={14} />
                        Cerrar conversación
                      </button>
                    )}
                    <button
                      className={btnDeleteSoft}
                      disabled={deleting || sending}
                      onClick={() => {
                        setConfirmRowId(null);
                        setConfirmHead('delete');
                      }}
                    >
                      <Trash2 size={14} />
                      Eliminar conversación
                    </button>
                  </div>
                )}
              </Can>
            )}
          </div>

          {error && (
            <div className="px-4 pt-3">
              <div className={errorCls}>
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  title="Ocultar aviso"
                  aria-label="Ocultar aviso"
                  className="shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          <div
            ref={threadRef}
            onScroll={onThreadScroll}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[40vh] max-h-[60vh]"
          >
            {historyLoading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
                <Loader2 size={14} className="animate-spin" />
                Cargando conversación…
              </div>
            ) : messages.length === 0 && !streaming ? (
              <div className="text-sm text-[var(--color-text-muted)]">
                {current
                  ? 'Esta conversación todavía no tiene mensajes.'
                  : 'Escribí abajo para empezar una conversación nueva.'}
              </div>
            ) : (
              messages.map((m) => (
                <Bubble
                  key={m.id}
                  m={m}
                  alerts={alerts}
                  onFollow={(id) => {
                    stickRef.current = true;
                    void followAlert(id);
                  }}
                  onDismiss={(id) => void dismissAlert(id)}
                  busy={alertBusy}
                  streaming={streaming || sending}
                  foreign={foreign}
                />
              ))
            )}

            {streaming && (
              <div className="flex justify-start">
                <div className="max-w-[85%] space-y-1">
                  {liveTools.map((name, i) => (
                    <ToolLine key={i} name={name} />
                  ))}
                  {liveText ? (
                    <div className="rounded-lg px-3 py-2 text-sm break-words bg-[var(--color-bg-secondary)] text-[var(--color-text)]">
                      <AssistantMarkdown text={liveText} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
                      <Loader2 size={14} className="animate-spin" />
                      Pensando…
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {lastUsage && (
            <div className="px-4 py-1 text-xs text-[var(--color-text-muted)] border-t border-[var(--color-border)]">
              Último turno: {lastUsage.usage.tokens_in.toLocaleString('es-AR')} tokens de entrada,{' '}
              {lastUsage.usage.tokens_out.toLocaleString('es-AR')} de salida,{' '}
              {(lastUsage.duration_ms / 1000).toLocaleString('es-AR', { maximumFractionDigits: 1 })}{' '}
              s
            </div>
          )}

          {current && (
            <ActionsPanel
              pending={pendingActions}
              recent={recentActions}
              outcome={actionOutcome}
              onDismissOutcome={() => setActionOutcome(null)}
              readOnly={foreign}
              busyId={actionBusy}
              resolvingId={resolvingBusy}
              onConfirm={(id) => void confirmAction(id)}
              onCancel={(id) => void cancelAction(id)}
              onLoadClaimOptions={loadClaimOptions}
              onResolveClaim={resolveClaim}
            />
          )}

          <div className="border-t border-[var(--color-border)] p-3">
            <Can
              cap="assistant.chat"
              fallback={
                <div className="text-sm text-[var(--color-text-muted)]">
                  No tenés permiso para conversar con el asistente.
                </div>
              }
            >
              {foreign ? (
                <div className="text-sm text-[var(--color-text-muted)]">
                  Conversación de otro usuario (solo lectura).
                </div>
              ) : closed ? (
                <div className="text-sm text-[var(--color-text-muted)]">
                  Esta conversación está cerrada. Empezá una nueva para seguir.
                </div>
              ) : (
                <div className="space-y-2">
                  <textarea
                    value={draft}
                    rows={rows}
                    aria-label="Mensaje para el asistente"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        void doSend();
                      }
                    }}
                    placeholder="Escribile al asistente… (Ctrl+Enter para enviar)"
                    className="w-full px-3 py-2 text-sm bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)] resize-none"
                  />
                  <div className="flex justify-end gap-2">
                    {streaming && !foreign && (
                      <button className={btnGhost} onClick={() => void stop()}>
                        <Square size={14} />
                        Detener
                      </button>
                    )}
                    <button
                      className={btnPrimary}
                      disabled={!canSend}
                      onClick={() => void doSend()}
                    >
                      <Send size={14} />
                      Enviar
                    </button>
                  </div>
                </div>
              )}
            </Can>
          </div>
        </div>
      </div>
    </div>
  );
}
